import { collectFilteredBranchItems } from "./branchProjection";
import { EMPTY_FLUSH_RESULT } from "./constants";
import { planLoad } from "./loadPlanner";
import { collectLeafItems, sameRequestKey } from "./pagination.utils";
import { PaginatedNode } from "./PaginatedNode";
import { planTrims } from "./trimPlanner";
import { ROOT_NODE_ID } from "./types";
import type {
  FlushResult,
  ID,
  LoadRequest,
  LoadResult,
  Page,
  PageFetcher,
  PageTrimOp,
  PaginationOptions,
  PaginationStats,
  ParentChain,
  ParentItem,
  PendingSnapshot,
} from "./types";

/** Bundled args for `applyFetchResult`/`markExhaustionForEmptyFetch`. */
interface ApplyFetchResultArgs {
  readonly load: LoadRequest;
  readonly items: unknown[];
  readonly beforeCursor: string | null;
  readonly afterCursor: string | null;
  /**
   * Absolute index of `items[0]` within the parent scope. Forwarded
   * verbatim to `PagedList.addPage`/`replacePageItems` to seed
   * `Page<T>.firstItemOffsetInScope`. Undefined for non-leaf depths
   * (group fetchers don't populate it).
   */
  readonly startIndex?: number;
}
interface MarkExhaustionArgs extends ApplyFetchResultArgs {
  readonly node: PaginatedNode;
}

/** Bundled args for `runLoadCycle`. */
interface RunLoadCycleArgs {
  readonly load: LoadRequest;
  readonly signal: AbortSignal;
  readonly result: FlushResult;
}

/** Bundled args for `runTrimCycle`. */
interface RunTrimCycleArgs {
  readonly visibleIds: ReadonlySet<ID>;
  readonly result: FlushResult;
}

/** Payload for `commitChange` — the per-cycle delta announced via `onChange`. */
interface CommitChangePayload {
  readonly trims: PageTrimOp[];
  readonly loads: LoadResult[];
}

/**
 * Framework-agnostic pagination orchestrator. Returns `LoadRequest`
 * objects for the adapter to fetch — does NOT fetch itself. Latest-
 * snapshot model: `update()` overwrites pending; `process()` drains.
 */
export class PaginationController {
  private readonly tree: PaginatedNode;
  private readonly options: PaginationOptions;
  private readonly nodeIndex: Map<ID, PaginatedNode>;
  /** Maps item id → id of the node that holds it. O(1) lookup for replaceItem/removeItem. */
  private readonly itemToNodeIndex: Map<ID, ID>;
  private pending: PendingSnapshot | null;
  private loading: boolean;
  private readonly cleanedCollapsed: Set<ID>;
  /** Set by `cleanCollapsed`; consumed by `process()` to emit a no-op `onChange`. */
  private collapseSetDirty: boolean;
  private leafItemsCache: unknown[] | null;
  private branchItemsCache: unknown[] | null;
  private abortController: AbortController;
  private processing: boolean;
  private processingPromise: Promise<FlushResult>;
  private dataEpoch: number;
  /** True while inside a `batchUpdate` call — suppresses intermediate emitMutation calls. */
  private _batching: boolean;
  /** True when emitMutation was called during a batch — triggers a single emit on batch end. */
  private _pendingEmit: boolean;

  constructor(options: PaginationOptions) {
    this.options = options;
    const rootGetItemKey = options.config.length
      ? (item: unknown) => options.config[0].getItemId(item as never)
      : (item: unknown) => String(item);

    this.tree = new PaginatedNode({
      id: ROOT_NODE_ID,
      depth: 0,
      getItemKey: rootGetItemKey,
    });

    this.nodeIndex = new Map([[ROOT_NODE_ID, this.tree]]);
    this.itemToNodeIndex = new Map();
    this.pending = null;
    this.loading = false;
    this.cleanedCollapsed = new Set();
    this.collapseSetDirty = false;
    this.leafItemsCache = null;
    this.branchItemsCache = null;
    this.abortController = new AbortController();
    this.processing = false;
    this.processingPromise = Promise.resolve(EMPTY_FLUSH_RESULT);
    this.dataEpoch = 0;
    this._batching = false;
    this._pendingEmit = false;
  }

  /** Store the latest visibility snapshot and kick processing (overwrites pending). */
  update(visibleIds: Set<ID>, collapsedIds = new Set<ID>()): void {
    this.pending = { visibleIds, collapsedIds };
    this.cleanCollapsed(collapsedIds);
    this.kickIfPending();
  }

  /** Reset to fresh state: empty tree, no caches, fresh AbortController. */
  clear(): void {
    this.fireDropForAllLeafPages();
    this.tree.clearItems();
    this.nodeIndex.clear();
    this.nodeIndex.set(ROOT_NODE_ID, this.tree);
    this.itemToNodeIndex.clear();
    this.pending = null;
    this.loading = false;
    this.processing = false;
    this.cleanedCollapsed.clear();
    this.collapseSetDirty = false;
    this.leafItemsCache = null;
    this.branchItemsCache = null;
    this.dataEpoch = 0;
    this.abort();
    if (this._batching) {
      this._pendingEmit = true;
    }
  }

  /** Index of the leaf level (`config.length - 1`). */
  getMaxDepth(): number {
    return this.maxDepth;
  }

  /** True when there is a deeper level after `depth` in the configured tree. */
  hasNextLevel(depth: number): boolean {
    return depth + 1 < this.options.config.length;
  }

  /** Flat sequence of leaf items in document order. Memoised across cycles. */
  getLeafItems(): unknown[] {
    this.leafItemsCache ??= collectLeafItems(this.tree, this.maxDepth);
    return this.leafItemsCache;
  }

  /**
   * Filtered branch-items projection for the Grid's `groups` prop.
   * See `docs/filtered-branch-items.md`.
   */
  getBranchItems(): unknown[] {
    if (this.branchItemsCache === null) {
      const acc: unknown[] = [];
      collectFilteredBranchItems(this.tree, acc, {
        collapsedIds: this.cleanedCollapsed,
        maxDepth: this.maxDepth,
      });
      this.branchItemsCache = acc;
    }
    return this.branchItemsCache;
  }

  /** O(1) node lookup by id; returns `undefined` for unmaterialized ids. */
  findNode(id: ID): PaginatedNode | undefined {
    return this.nodeIndex.get(id);
  }

  /**
   * Absolute index of `itemId` within its node's scope, derived from the
   * page's `firstItemOffsetInScope` plus the item's position inside that
   * page. Returns `null` when the item is not loaded, or when the
   * containing page carries a `null` offset (non-leaf depth, or backend
   * omitted `start_index`).
   *
   * Encapsulates the page-walk so consumers don't reach into
   * `controller.findNode(id).pages.pages[0]` directly (Law of Demeter).
   */
  getItemOffset(itemId: ID): number | null {
    const nodeId = this.itemToNodeIndex.get(itemId);
    if (nodeId === undefined) {
      return null;
    }
    const node = this.nodeIndex.get(nodeId);
    if (!node) {
      return null;
    }
    for (const page of node.pages.pages) {
      const indexInPage = page.itemIds.indexOf(itemId);
      if (indexInPage === -1) {
        continue;
      }
      if (page.firstItemOffsetInScope === null) {
        return null;
      }
      return page.firstItemOffsetInScope + indexInPage;
    }
    return null;
  }

  /**
   * Absolute index of the first item currently loaded under `nodeId`,
   * read from the first page's `firstItemOffsetInScope`. Returns `null`
   * when the node is unknown, has no loaded pages, or its first page
   * has a `null` offset (non-leaf depth).
   */
  getNodeFirstItemOffset(nodeId: ID): number | null {
    const node = this.nodeIndex.get(nodeId);
    if (!node) {
      return null;
    }
    const firstPage = node.pages.pages[0];
    if (!firstPage) {
      return null;
    }
    return firstPage.firstItemOffsetInScope;
  }

  /**
   * Snapshot of pagination state for debug / monitoring consumers. Single
   * O(N) pass over the live node index — never throws, never mutates.
   */
  getStats(): PaginationStats {
    const maxDepth = this.maxDepth;
    let totalPages = 0;
    let leafPages = 0;
    let leafNodes = 0;
    let branchNodes = 0;
    let leafItemsCount = 0;

    for (const node of this.nodeIndex.values()) {
      const pageCount = node.pages.pages.length;
      totalPages += pageCount;
      if (node.depth === maxDepth) {
        leafNodes += 1;
        leafPages += pageCount;
        leafItemsCount += node.pages.items.length;
      } else {
        branchNodes += 1;
      }
    }

    return {
      totalPages,
      leafPages,
      branchPages: totalPages - leafPages,
      totalNodes: this.nodeIndex.size,
      leafNodes,
      branchNodes,
      leafItemsCount,
      maxDepth,
      epoch: this.dataEpoch,
      isLoading: this.loading,
      isIdle: !this.processing && !this.loading && this.pending === null,
      hasPending: this.pending !== null,
      maxPages: this.options.maxPages,
      edgeThreshold: this.options.edgeThreshold,
      pageSizes: this.options.config.map((fetcher) => fetcher.pageSize),
    };
  }

  /**
   * Mark loaded nodes as stale so `walkNode` reloads them lazily when
   * reached. Nodes whose pages hold zero items are reset to a fresh
   * bootstrap-eligible state instead — `tryStaleReload` cannot fire on
   * an empty page, so without the reset such nodes would stay stuck.
   */
  invalidateLoadedData(depth?: number): void {
    for (const node of this.nodeIndex.values()) {
      if (depth !== undefined && node.depth !== depth) {
        continue;
      }
      if (node.pages.items.length === 0) {
        node.resetForBootstrap();
      } else {
        node.markStale();
      }
    }
    this.invalidateCache();
    this.kickIfPending();
  }

  /**
   * Optimistically replace a single loaded item. O(1) via itemToNodeIndex.
   * Returns false if the item is not in any loaded page.
   */
  replaceItem(itemId: ID, newItem: unknown): boolean {
    const nodeId = this.itemToNodeIndex.get(itemId);
    const node = nodeId !== undefined ? this.nodeIndex.get(nodeId) : undefined;
    if (!node) {
      return false;
    }

    const replaced = node.replaceItem(itemId, newItem as never);
    if (!replaced) {
      return false;
    }

    this.emitMutation();
    return true;
  }

  /**
   * Remove a single loaded item and apply cursor smoothing to all
   * dependent pages in the tree. O(1) lookup + O(nodes) cursor walk.
   * Returns false if the item is not in any loaded page.
   */
  removeItem(itemId: ID): boolean {
    const nodeId = this.itemToNodeIndex.get(itemId);
    const node = nodeId !== undefined ? this.nodeIndex.get(nodeId) : undefined;
    if (!node) {
      return false;
    }

    const { removed, prevId, nextId } = node.removeItem(itemId);
    if (!removed) {
      return false;
    }

    this.itemToNodeIndex.delete(itemId);

    for (const n of this.nodeIndex.values()) {
      n.rewriteRequestCursorReferences(itemId, prevId, nextId);
    }

    // Drop a leaf node that just lost its last item — same logic as
    // cross-leaf eviction in `dropFromPreviousOwner`. Scoped to leaves
    // so intermediate nodes survive (their server-side composition is
    // unknown until a parent reload).
    if (node.depth === this.maxDepth && node.pages.items.length === 0) {
      this.dropEmptyLeaf(node);
    }

    this.emitMutation();
    return true;
  }

  /**
   * Execute `fn` while suppressing intermediate `onChange` emissions.
   * Fires exactly one `onChange` after `fn` completes (if any mutation
   * occurred inside). Useful for atomic multi-step operations like
   * `clear()` + two `applyFetchResult` calls.
   */
  batchUpdate(fn: () => void): void {
    this._batching = true;
    this._pendingEmit = false;
    try {
      fn();
    } finally {
      this._batching = false;
      if (this._pendingEmit) {
        this._pendingEmit = false;
        this.dataEpoch++;
        this.invalidateCache();
        this.options.onChange?.({ stateChanged: true, trims: [], loads: [] });
      }
    }
  }

  /** Resolve once no work is in flight (drains recursive cycles). @internal */
  async whenIdle(): Promise<FlushResult> {
    let result: FlushResult;
    do {
      result = await this.processingPromise;
    } while (this.processing || this.pending);
    return result;
  }

  /** Abort in-flight fetches; the next fetch uses a fresh AbortController. @internal */
  abort(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  /** @internal */
  get isLoading(): boolean {
    return this.loading;
  }

  /** @internal */
  get isIdle(): boolean {
    return !this.processing && !this.loading && this.pending === null;
  }

  /** @internal */
  get hasPending(): boolean {
    return this.pending !== null;
  }

  /** Monotonically increasing version; bumped after each stateChanged cycle. @internal */
  get currentEpoch(): number {
    return this.dataEpoch;
  }

  /** @internal */
  getTree(): PaginatedNode {
    return this.tree;
  }

  /**
   * Fetch the page for `load` and apply it to the tree. Returns
   * "applied" (state changed), "skipped" (duplicate / unknown node /
   * fetch error), or "aborted" (cancelled via AbortController).
   */
  private async executeFetch(load: LoadRequest): Promise<"applied" | "skipped" | "aborted"> {
    const signal = this.abortController.signal;
    this.loading = true;
    this.options.onLoadingChange?.(load);
    const node = this.nodeIndex.get(load.nodeId);
    node?.markLoading();
    this.invalidateCache();

    try {
      const fetcher = this.getFetcher(load.depth);
      const parents = buildParentChain(node);
      const result = await fetcher.fetch(parents, {
        key: load.key,
        pageSize: load.pageSize,
        signal,
        extraData: node?.extraData,
      });

      if (signal.aborted) {
        return "aborted";
      }

      const applied = this.applyFetchResult({
        load,
        items: result.items,
        beforeCursor: result.beforeCursor,
        afterCursor: result.afterCursor,
        startIndex: result.startIndex,
      });

      return applied ? "applied" : "skipped";
    } catch {
      if (signal.aborted) {
        return "aborted";
      }
      return "skipped";
    } finally {
      this.loading = false;
      node?.markLoaded();
      this.options.onLoadingChange?.(null);
      this.kickIfPending();
    }
  }

  /**
   * Process the latest pending snapshot. One cycle is mutually
   * exclusive: load OR trim OR collapse-only (never both load+trim).
   * Trim batches may carry ops for both sides in the same cycle.
   *
   *   drainPending()
   *      │
   *      ├── load? ──→ runLoadCycle  (fetch + apply + onChange)
   *      ├── trims? ─→ runTrimCycle  (apply + onChange)
   *      └── collapseDirty? → commitCollapseOnlyChange  (onChange only)
   */
  private async process(): Promise<FlushResult> {
    if (this.processing) {
      return EMPTY_FLUSH_RESULT;
    }
    this.processing = true;
    const signal = this.abortController.signal;

    const result: FlushResult = { stateChanged: false, trims: [], loads: [] };
    try {
      if (signal.aborted) {
        return result;
      }

      const { load, visibleIds } = this.drainPending();

      if (load) {
        await this.runLoadCycle({ load, signal, result });
        return result;
      }
      if (visibleIds && this.runTrimCycle({ visibleIds, result })) {
        return result;
      }
      if (this.collapseSetDirty) {
        this.commitCollapseOnlyChange(result);
      }
    } finally {
      this.processing = false;
      this.options.onProcessComplete?.(result);
      this.kickIfPending();
    }

    return result;
  }

  /** Pop the pending snapshot + compute the load. Returns nulls when not drainable. */
  private drainPending(): {
    readonly load: LoadRequest | null;
    readonly visibleIds: ReadonlySet<ID> | null;
  } {
    if (this.loading || !this.pending) {
      return { load: null, visibleIds: null };
    }
    const entry = this.pending;
    this.pending = null;
    return {
      load: this.computeLoad(entry.visibleIds, entry.collapsedIds),
      visibleIds: entry.visibleIds,
    };
  }

  /** Run one load cycle: fetch, apply, commit. Aborts/dups skip the commit. */
  private async runLoadCycle(args: RunLoadCycleArgs): Promise<void> {
    const { load, signal, result } = args;
    const status = await this.executeFetch(load);
    if (signal.aborted) {
      return;
    }
    result.loads.push({ request: load, status });
    if (status !== "applied") {
      return;
    }
    this.commitChange(result, { trims: [], loads: [{ request: load, status }] });
  }

  /** Run one trim cycle. Returns true when at least one page was trimmed. */
  private runTrimCycle(args: RunTrimCycleArgs): boolean {
    const { visibleIds, result } = args;
    const trims = planTrims({
      root: this.tree,
      visibleIds,
      cleanedCollapsed: this.cleanedCollapsed,
      maxDepth: this.maxDepth,
      maxPages: this.options.maxPages,
      getFetcher: (depth) => this.getFetcher(depth),
    });
    if (trims.length === 0) {
      return false;
    }
    for (const trim of trims) {
      this.trimNodePage(trim.nodeId, trim.from);
    }
    result.trims.push(...trims);
    this.commitChange(result, { trims, loads: [] });
    return true;
  }

  /**
   * Fire an onChange for a WS-driven mutation (replaceItem / removeItem).
   * If inside a batchUpdate, set the pending flag instead so the batch
   * fires a single onChange when it finishes.
   */
  private emitMutation(): void {
    if (this._batching) {
      this._pendingEmit = true;
      return;
    }
    this.dataEpoch++;
    this.invalidateCache();
    this.options.onChange?.({ stateChanged: true, trims: [], loads: [] });
  }

  /** Bookkeeping shared by load and trim cycles: epoch++, dirty clear, onChange. */
  private commitChange(result: FlushResult, payload: CommitChangePayload): void {
    result.stateChanged = true;
    this.dataEpoch++;
    this.collapseSetDirty = false;
    this.options.onChange?.({ stateChanged: true, ...payload });
  }

  /** Idle-cycle commit when only the collapsed set changed (no load/trim). */
  private commitCollapseOnlyChange(result: FlushResult): void {
    this.collapseSetDirty = false;
    result.stateChanged = true;
    this.dataEpoch++;
    this.options.onChange?.({ stateChanged: true, trims: [], loads: [] });
  }

  /** Re-fire `process()` when a fresh snapshot is queued and nothing's in flight. */
  private kickIfPending(): void {
    if (this.pending && !this.processing && !this.loading) {
      this.processingPromise = this.process();
    }
  }

  /** Reconcile the collapsed set, then delegate the load decision to `planLoad`. */
  private computeLoad(
    visibleIds: ReadonlySet<ID>,
    collapsedIds: ReadonlySet<ID>,
  ): LoadRequest | null {
    this.cleanCollapsed(collapsedIds);

    return planLoad({
      root: this.tree,
      visibleIds,
      collapsedIds,
      cleanedCollapsed: this.cleanedCollapsed,
      edgeThreshold: this.options.edgeThreshold,
      getFetcher: (depth) => this.getFetcher(depth),
      hasNextLevel: (depth) => this.hasNextLevel(depth),
      branchProjectionCtx: { collapsedIds: this.cleanedCollapsed, maxDepth: this.maxDepth },
    });
  }

  /**
   * Reconcile `cleanedCollapsed` with the latest snapshot. Newly-collapsed
   * parents have their subtrees cleared (so re-expand re-fetches), and the
   * dirty flag is raised so `process()` emits an `onChange` on idle cycles.
   */
  private cleanCollapsed(collapsedIds: ReadonlySet<ID>): void {
    let setChanged = false;

    for (const nodeId of this.cleanedCollapsed) {
      if (!collapsedIds.has(nodeId)) {
        this.cleanedCollapsed.delete(nodeId);
        // Mark the just-expanded empty node as loading so the chain
        // rule keeps siblings exposed until records arrive, and as
        // `justExpanded` so descent emits a forward bootstrap instead
        // of step (2c)'s `fromLast` redirect (the latter is meant for
        // scroll-up; expand wants the FIRST page, not the LAST).
        const expandedNode = this.nodeIndex.get(nodeId);
        if (expandedNode?.pages.isEmpty) {
          expandedNode.markLoading();
          expandedNode.markJustExpanded();
        }
        setChanged = true;
      }
    }

    for (const nodeId of collapsedIds) {
      if (this.cleanedCollapsed.has(nodeId)) {
        continue;
      }
      const node = this.nodeIndex.get(nodeId);
      if (!node) {
        continue;
      }
      if (!node.hasChildren) {
        this.cleanedCollapsed.add(nodeId);
        setChanged = true;
        continue;
      }

      this.clearSubtree(node);
      this.cleanedCollapsed.add(nodeId);
      setChanged = true;
      this.invalidateCache();
    }

    if (setChanged) {
      this.branchItemsCache = null;
      this.collapseSetDirty = true;
    }
  }

  /**
   * Apply fetch result to the tree (ADDS items unless `reload`); creates
   * child nodes when the depth has a next level. False on unknown node /
   * all-duplicates. Public so loader can call it inside batchUpdate.
   */
  applyFetchResult(args: ApplyFetchResultArgs): boolean {
    const { load, items, beforeCursor, afterCursor, startIndex } = args;
    const node = this.nodeIndex.get(load.nodeId);
    if (!node) {
      return false;
    }

    const fetcher = this.getFetcher(load.depth);

    if (load.reload) {
      // Remove old page items from the index before replacing — but only
      // entries still pointing at THIS node. A previous fetch for a sibling
      // leaf (e.g. records moved between groups) may have already remapped
      // some ids to their new owners, and we mustn't blow that away.
      const oldPage = node.pages.pages.find((p) => sameRequestKey(p.requestCursor, load.key));
      if (oldPage) {
        for (const id of oldPage.itemIds) {
          if (this.itemToNodeIndex.get(id) === node.id) {
            this.itemToNodeIndex.delete(id);
          }
        }
      }
      if (oldPage) {
        this.fireLeafPageDropped(node, [oldPage]);
      }
      node.replacePageItems({
        requestCursor: load.key,
        newItems: items,
        beforeCursor,
        afterCursor,
        startIndex,
      });
      for (const item of items) {
        const itemId = fetcher.getItemId(item);
        this.dropFromPreviousOwner(itemId, node.id, load.depth);
        this.itemToNodeIndex.set(itemId, node.id);
      }
      const preexistingChildIds = new Set<ID>();
      for (const item of items) {
        const childId = node.getItemKey(item);
        if (node.hasChild(childId)) {
          preexistingChildIds.add(childId);
        }
      }
      this.materializeChildren(node, items);
      if (this.hasNextLevel(node.depth)) {
        for (const item of items) {
          const childId = node.getItemKey(item);
          if (preexistingChildIds.has(childId)) {
            continue;
          }
          const child = node.getChild(childId);
          child?.markLoading();
        }
      }
      this.invalidateCache();
      const newPage = node.pages.pages.find((p) => sameRequestKey(p.requestCursor, load.key));
      if (newPage) {
        this.fireLeafPageLoaded(node, [newPage]);
      }
      if (this._batching) {
        this._pendingEmit = true;
      }
      return true;
    }

    const added = node.addItems({
      items,
      requestCursor: load.key,
      beforeCursor,
      afterCursor,
      direction: load.direction,
      startIndex,
    });
    if (!added) {
      this.markExhaustionForEmptyFetch({ node, load, items, beforeCursor, afterCursor });
      return false;
    }

    for (const item of items) {
      const itemId = fetcher.getItemId(item);
      this.dropFromPreviousOwner(itemId, node.id, load.depth);
      this.itemToNodeIndex.set(itemId, node.id);
    }

    this.materializeChildren(node, items);
    this.invalidateCache();

    const addedPage = node.pages.pages.find((p) => sameRequestKey(p.requestCursor, load.key));
    if (addedPage) {
      this.fireLeafPageLoaded(node, [addedPage]);
    }

    if (this._batching) {
      this._pendingEmit = true;
    }

    return true;
  }

  /**
   * Make sure an item lives in only one node at a time.
   *
   * Call this right before assigning an item to a new owner node. If the
   * same item id is currently registered to a different node at the same
   * depth — typically because its parent group changed and a stale copy
   * is still sitting in the old node's pages — remove that stale copy.
   *
   * Without this, `getLeafItems()` would return the same record twice
   * during the brief window when both the new and old leaves still hold
   * it, and the grid would render overlapping rows.
   */
  private dropFromPreviousOwner(itemId: ID, newNodeId: ID, depth: number): void {
    const prevNodeId = this.itemToNodeIndex.get(itemId);
    if (!prevNodeId || prevNodeId === newNodeId) {
      return;
    }
    const prevNode = this.nodeIndex.get(prevNodeId);
    if (prevNode?.depth !== depth) {
      return;
    }
    prevNode.removeItem(itemId);

    if (depth === this.maxDepth && prevNode.pages.items.length === 0) {
      this.dropEmptyLeaf(prevNode);
    }
  }

  /**
   * Detach an empty leaf from the tree the moment its last item leaves.
   *
   * Without this, the leaf would remain reachable from its parent's
   * pages until the parent's next reload returns an item list that no
   * longer mentions it — leaving consumers with a node whose
   * `pages.items.length === 0` that still appears in
   * `getBranchItems()` / `getLeafItems()` walks.
   *
   * After detaching the leaf, the cleanup walks up the tree: if the
   * parent now has zero items AND we already know the data source
   * has nothing more for it on either side (both `canLoad` flags
   * were `false` before the removal), the parent is detached too,
   * and the walk continues. The walk stops at the first ancestor
   * that still has items, might still load more, is mid-fetch, or
   * is the root.
   *
   * The `canLoad` flags are read *before* calling `parent.removeItem`
   * on purpose: removing the last item in a page triggers the page
   * to be spliced out, which resets the overlay to a fresh
   * `hasMore = true` state. Reading `canLoad` afterwards would make
   * a just-emptied node look like one that has never been fetched,
   * and the cascade decision would be wrong.
   */
  private dropEmptyLeaf(leaf: PaginatedNode): void {
    let current: PaginatedNode | null = leaf;
    while (current !== null) {
      const parent: PaginatedNode | null = current.parent;
      if (parent === null) {
        // current is the root (or an already-detached node) — stop.
        break;
      }
      const parentHadMoreToLoad = parent.pages.canLoad("after") || parent.pages.canLoad("before");
      const { removed } = parent.removeItem(current.id);
      parent.removeChild(current.id);
      this.removeFromIndex(current);

      const parentIsDrained =
        removed && parent.pages.items.length === 0 && !parent.loading && !parentHadMoreToLoad;
      if (!parentIsDrained) {
        break;
      }
      current = parent;
    }
    this.invalidateCache();
  }

  /**
   * Create child PaginatedNodes for newly fetched items when the depth
   * has the next level. Each child starts with a fresh empty PagedList;
   * step (0) bootstraps it (or step 2c's `fromLast`) on next visibility.
   */
  private materializeChildren(node: PaginatedNode, items: unknown[]): void {
    if (!this.hasNextLevel(node.depth)) {
      return;
    }

    const childDepth = node.depth + 1;
    const fetcher = this.getFetcher(node.depth);
    const childFetcher = this.getFetcher(childDepth);
    const childGetItemKey = (item: unknown): ID => childFetcher.getItemId(item);

    for (const item of items) {
      const childId = node.getItemKey(item);
      if (node.hasChild(childId)) {
        continue;
      }

      const extraData = fetcher.getItemExtraData?.(item) ?? {};
      const child = new PaginatedNode({
        id: childId,
        depth: childDepth,
        getItemKey: childGetItemKey,
        extraData,
      });

      node.addChild(child);
      this.nodeIndex.set(child.id, child);
    }
  }

  /**
   * Mark the matching side exhausted on a zero-item fetch with `null`
   * boundary cursor. The early `items.length !== 0` return distinguishes
   * the "all-duplicates" case from real exhaustion — `addItems()` returns
   * false for both, but only the empty-fetch case means the side is dead.
   * Marking exhausted on duplicates would lose data.
   */
  private markExhaustionForEmptyFetch(args: MarkExhaustionArgs): void {
    const { node, load, items, beforeCursor, afterCursor } = args;
    // All-duplicates branch (cursor advanced) — do not mark exhausted.
    if (items.length !== 0) {
      return;
    }
    if (load.direction === "after" && afterCursor === null) {
      node.markAfterExhausted();
      return;
    }
    if (load.direction === "before" && beforeCursor === null) {
      node.markBeforeExhausted();
    }
  }

  /** Trim one page from a node; evicts child subtrees for the removed items. */
  private trimNodePage(nodeId: ID, from: "top" | "bottom"): boolean {
    const node = this.nodeIndex.get(nodeId);
    if (!node || node.pages.isEmpty) {
      return false;
    }

    const removedPage =
      from === "top" ? node.pages.pages[0] : node.pages.pages[node.pages.length - 1];

    // Remove trimmed items from itemToNodeIndex
    for (const id of removedPage.itemIds) {
      this.itemToNodeIndex.delete(id);
    }

    if (node.hasChildren) {
      for (const evictedId of removedPage.itemIds) {
        const evictedChild = node.removeChild(evictedId);
        if (evictedChild) {
          this.removeFromIndex(evictedChild);
        }
      }
    }

    node.removeItemsPage(from);
    this.fireLeafPageDropped(node, [removedPage]);
    this.invalidateCache();

    return true;
  }

  /** Wipe descendants on collapse so re-expand bootstraps a fresh subtree. */
  private clearSubtree(node: PaginatedNode): void {
    for (const child of node.childValues()) {
      this.removeFromIndex(child);
      child.clearItems();
      this.nodeIndex.set(child.id, child);
    }
  }

  /** Remove `node` and all its descendants from `nodeIndex` and `itemToNodeIndex`. */
  private removeFromIndex(node: PaginatedNode): void {
    if (node.depth === this.maxDepth) {
      this.fireLeafPageDropped(node, node.pages.pages);
    }
    this.nodeIndex.delete(node.id);
    for (const page of node.pages.pages) {
      for (const id of page.itemIds) {
        this.itemToNodeIndex.delete(id);
      }
    }
    for (const child of node.childValues()) {
      this.removeFromIndex(child);
    }
  }

  /** Drop the leaf/branch projection caches; recomputed lazily on next read. */
  private invalidateCache(): void {
    this.leafItemsCache = null;
    this.branchItemsCache = null;
  }

  /**
   * Fire the page-loaded callback that matches the node's depth:
   *
   * - `onLeafPageLoaded` when `node.depth === maxDepth` (the lowest
   *   level — records).
   * - `onBranchPageLoaded` when `node.depth < maxDepth` (intermediate
   *   group levels).
   *
   * No-op when the corresponding callback is not provided or `pages`
   * is empty.
   */
  private firePageLoaded(node: PaginatedNode, pages: ReadonlyArray<Page<unknown>>): void {
    if (pages.length === 0) {
      return;
    }
    if (node.depth === this.maxDepth) {
      this.options.onLeafPageLoaded?.(pages, node.id);
      return;
    }
    this.options.onBranchPageLoaded?.(pages, node.id);
  }

  /** Symmetric to {@link firePageLoaded}. */
  private firePageDropped(node: PaginatedNode, pages: ReadonlyArray<Page<unknown>>): void {
    if (pages.length === 0) {
      return;
    }
    if (node.depth === this.maxDepth) {
      this.options.onLeafPageDropped?.(pages, node.id);
      return;
    }
    this.options.onBranchPageDropped?.(pages, node.id);
  }

  /** Backward-compat aliases — preserved so existing call sites stay terse. */
  private fireLeafPageLoaded(node: PaginatedNode, pages: ReadonlyArray<Page<unknown>>): void {
    this.firePageLoaded(node, pages);
  }

  private fireLeafPageDropped(node: PaginatedNode, pages: ReadonlyArray<Page<unknown>>): void {
    this.firePageDropped(node, pages);
  }

  /** Drop every resident page (both leaf and branch); called by `clear()`. */
  private fireDropForAllLeafPages(): void {
    if (!this.options.onLeafPageDropped && !this.options.onBranchPageDropped) {
      return;
    }
    for (const node of this.nodeIndex.values()) {
      this.firePageDropped(node, node.pages.pages);
    }
  }

  /**
   * Per-depth fetcher, clamped to the last config entry when `depth`
   * exceeds the configured levels. Single source of truth so every
   * fetch site uses the same clamp rule.
   */
  private getFetcher(depth: number): PageFetcher {
    const idx = Math.min(depth, this.options.config.length - 1);
    return this.options.config[idx];
  }

  /** Index of the leaf level (`config.length - 1`). */
  private get maxDepth(): number {
    return this.options.config.length - 1;
  }
}

/**
 * Project the parent node and its ancestors into a depth-ordered
 * {@link ParentChain}. The implicit root node carries no useful `extraData`
 * and is excluded, so a fetch targeting the root's direct children receives
 * an empty array.
 *
 *   parents[0]                    — depth 0 ancestor (outermost group)
 *   parents[parents.length - 1]   — immediate parent (the node whose
 *                                   children we're about to fetch)
 */
function buildParentChain(node: PaginatedNode | undefined): ParentChain {
  if (!node || node.id === ROOT_NODE_ID) {
    return [];
  }
  const chain: ParentItem[] = [];
  let cur: PaginatedNode | null = node;
  while (cur && cur.id !== ROOT_NODE_ID) {
    chain.push({ id: cur.id, extraData: cur.extraData });
    cur = cur.parent;
  }
  return chain.reverse();
}
